import React, { useMemo, useState } from 'react';

const parseJson = (value) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return typeof value === 'object' ? value : {};
};

const formatPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1).replace(/\.0$/, '');
};

const normalizeEvidence = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item) return '';
      if (typeof item === 'string') return item;
      if (typeof item === 'object') return item.text || item.evidence || item.value || JSON.stringify(item);
      return String(item);
    })
    .map((item) => item.trim())
    .filter(Boolean);
};

const getCandidateName = (candidate, index) =>
  candidate?.name || candidate?.candidate_name || candidate?.full_name || `Candidate ${index + 1}`;

const escapeCell = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const downloadRowsAsExcel = (rows, title) => {
  if (!rows.length) return;

  const headers = ['Candidate', 'Resume Match', 'Fit', 'Confidence', 'Criterion', 'Category', 'Weight', 'Rating', 'Contribution', 'Resume Evidence', 'Reason', 'Gap'];
  const bodyRows = rows.map((row) => [
    row.candidate,
    `${formatPercent(row.totalScore)}%`,
    row.fitBand,
    row.confidence,
    row.criterion,
    row.category,
    `${formatPercent(row.weight)}%`,
    row.rating !== undefined ? `${row.rating}/4` : '',
    `${formatPercent(row.weightedScore)}%`,
    row.evidence.join('\n'),
    row.reason,
    row.gap,
  ]);

  const html = `
    <html>
      <head><meta charset="UTF-8" /></head>
      <body>
        <table border="1">
          <thead><tr>${headers.map((header) => `<th>${escapeCell(header)}</th>`).join('')}</tr></thead>
          <tbody>
            ${bodyRows.map((row) => `<tr>${row.map((cell) => `<td>${escapeCell(cell)}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const safeTitle = String(title || 'score-details').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'score-details';
  link.href = url;
  link.download = `${safeTitle}-${new Date().toISOString().slice(0, 10)}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const candidateToneClasses = [
  'border-l-4 border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-900/45 dark:hover:bg-gray-900/65',
  'border-l-4 border-blue-400 bg-blue-50 hover:bg-blue-100 dark:border-blue-500 dark:bg-blue-950/35 dark:hover:bg-blue-950/50',
];

const ScoreEvidenceModal = ({ open, onClose, candidates = [], candidate = null, title = 'Resume Match Details', scoreKind = 'resume_match' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const isSingleCandidate = Boolean(candidate);
  const isResumeMatch = scoreKind === 'resume_match';

  const rows = useMemo(() => {
    const list = candidate ? [candidate] : candidates;
    return (Array.isArray(list) ? list : [])
      .map((item, candidateIndex) => {
        const details = parseJson(item?.score_details || item?.scoreDetails);
        const criteria = Array.isArray(details.criteria) ? details.criteria : [];
        return criteria.map((criterion, criterionIndex) => ({
          id: `${item?.id || item?.candidate_id || candidateIndex}-${criterion.criterion_id || criterion.label || criterionIndex}`,
          candidate: getCandidateName(item, candidateIndex),
          totalScore: details.total_score ?? item?.match_percentage ?? item?.matchPercentage,
          fitBand: details.fit_band || '',
          confidence: details.evidence_confidence || '',
          criterion: criterion.label || criterion.criterion || 'Criterion',
          category: criterion.category || '',
          weight: criterion.weight,
          rating: criterion.evidence_rating,
          componentScore: criterion.component_score,
          weightedScore: criterion.weighted_score,
          reason: criterion.reason || '',
          gap: criterion.gap || '',
          evidence: normalizeEvidence(criterion.resume_evidence || criterion.evidence),
          validationFlag: criterion.validation_flag || '',
          candidateIndex,
        }));
      })
      .flat();
  }, [candidate, candidates]);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return rows.filter((row) => {
      const ratingMatch = ratingFilter === 'all' || String(row.rating ?? '') === ratingFilter;
      const statusMatch = statusFilter === 'all' || String(row.validationFlag || '').toLowerCase() === statusFilter;
      const queryMatch = !query || [
        row.candidate,
        row.criterion,
        row.category,
        row.reason,
        row.gap,
        row.validationFlag,
        ...row.evidence,
      ].some((value) => String(value || '').toLowerCase().includes(query));
      return ratingMatch && statusMatch && queryMatch;
    });
  }, [ratingFilter, rows, searchTerm, statusFilter]);

  const statusOptions = useMemo(() => {
    const values = new Set(rows.map((row) => String(row.validationFlag || '').trim()).filter(Boolean));
    return Array.from(values);
  }, [rows]);

  const individualSummary = isSingleCandidate && rows.length > 0
    ? {
        name: rows[0].candidate,
        totalScore: rows[0].totalScore,
      }
    : null;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/50 p-3 sm:p-5" onClick={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-[98vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <span>{individualSummary ? `${title} - ${individualSummary.name}` : title}</span>
              {individualSummary && (
                <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
                  {formatPercent(individualSummary.totalScore)}%
                  {isResumeMatch ? ' resume match' : ''}
                </span>
              )}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {isResumeMatch
                ? `${individualSummary ? 'Resume/JD match explanation for this candidate' : `Resume/JD match explanation for ${new Set(rows.map((row) => row.candidate)).size} candidates`}. This is pre-interview fit evidence, not the AI interview result score.`
                : `${individualSummary ? 'Score explanation for this candidate' : `Score explanation for ${new Set(rows.map((row) => row.candidate)).size} candidates`}.`}
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => downloadRowsAsExcel(filteredRows, title)}
              disabled={filteredRows.length === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-900/50"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16" />
              </svg>
              Download Excel
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label="Close score details"
            >
              ×
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-900">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(240px,1fr)_160px_180px]">
            <label className="relative block">
              <span className="sr-only">Search score details</span>
              <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
              </svg>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search criteria, evidence, reason..."
                className="h-10 w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-gray-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-600 dark:focus:ring-blue-900/40"
              />
            </label>
            <select
              value={ratingFilter}
              onChange={(event) => setRatingFilter(event.target.value)}
              className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="all">All ratings</option>
              <option value="4">4/4</option>
              <option value="3">3/4</option>
              <option value="2">2/4</option>
              <option value="1">1/4</option>
              <option value="0">0/4</option>
            </select>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="all">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status.toLowerCase()}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-auto bg-gray-50/50 p-4 dark:bg-gray-950/30 sm:p-5">
          {rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              No score evidence is available yet.
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              No score evidence matched your filters.
            </div>
          ) : (
            <table className={`${isSingleCandidate ? 'min-w-[1100px]' : 'min-w-[1320px]'} overflow-hidden rounded-xl border border-gray-200 bg-white text-left text-xs shadow-sm dark:border-gray-700 dark:bg-gray-900`}>
              <thead className="sticky top-0 z-10 bg-gray-100 text-[11px] uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <tr>
                  {!isSingleCandidate && <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Candidate</th>}
                  {!isSingleCandidate && <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Resume match</th>}
                  {!isSingleCandidate && <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Fit</th>}
                  <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Criterion</th>
                  <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Weight</th>
                  <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Rating</th>
                  <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Points</th>
                  <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">{isResumeMatch ? 'Resume evidence' : 'Evidence'}</th>
                  <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Reason</th>
                  <th className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">Gap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredRows.map((row) => (
                  <tr key={row.id} className={`align-top text-gray-700 transition dark:text-gray-300 ${isSingleCandidate ? 'hover:bg-gray-50 dark:hover:bg-gray-800/60' : candidateToneClasses[row.candidateIndex % candidateToneClasses.length]}`}>
                    {!isSingleCandidate && <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{row.candidate}</td>}
                    {!isSingleCandidate && <td className="px-4 py-3 font-semibold">{formatPercent(row.totalScore)}%</td>}
                    {!isSingleCandidate && <td className="px-4 py-3">
                      <div className="space-y-1">
                        {row.fitBand && <span className="block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">{row.fitBand}</span>}
                        {row.confidence && <span className="block rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">{row.confidence}</span>}
                      </div>
                    </td>}
                    <td className="px-4 py-3">
                      <div className="font-semibold">{row.criterion}</div>
                      {row.category && <div className="mt-1 text-[10px] uppercase text-gray-400">{row.category}</div>}
                      {row.validationFlag && <div className="mt-1 text-[10px] text-gray-500">{row.validationFlag}</div>}
                    </td>
                    <td className="px-4 py-3">{formatPercent(row.weight)}%</td>
                    <td className="px-4 py-3">{row.rating !== undefined ? `${row.rating}/4` : '-'}</td>
                    <td className="px-4 py-3">{formatPercent(row.weightedScore)}%</td>
                    <td className="max-w-md px-4 py-3">
                      {row.evidence.length > 0 ? (
                        <div className="space-y-1">
                          {row.evidence.slice(0, 3).map((entry, index) => (
                            <div key={`${row.id}-evidence-${index}`} className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 leading-4 dark:border-gray-700 dark:bg-gray-800">
                              {entry}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">No evidence</span>
                      )}
                    </td>
                    <td className="max-w-sm px-4 py-3 leading-5">{row.reason || '-'}</td>
                    <td className="max-w-sm px-4 py-3 leading-5">
                      {row.gap ? (
                        <span className="font-semibold text-amber-700 dark:text-amber-300">{row.gap}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoreEvidenceModal;
